/**
 * Klondike solitaire rules. No DOM, no Node - the web board and a later Expo table
 * have to play by the same scoring and move checks.
 *
 * Scoring matches the legacy pasjans/script.js:
 *   +10  onto a foundation
 *   +5   waste onto a tableau
 *   +5   turning a face-down tableau card
 *  -100  recycling the waste back into the stock
 */

export const SUITS = ["♠", "♥", "♦", "♣"] as const;
export const VALUES = [
  "A",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
] as const;

export type Suit = (typeof SUITS)[number];
export type RankValue = (typeof VALUES)[number];
export type CardColor = "red" | "black";
export type DrawCount = 1 | 3;
export type FoundationIndex = 0 | 1 | 2 | 3;
export type TableauIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const FOUNDATION_INDEXES: readonly FoundationIndex[] = [0, 1, 2, 3];
export const TABLEAU_INDEXES: readonly TableauIndex[] = [0, 1, 2, 3, 4, 5, 6];

export interface PasjansCard {
  id: string;
  suit: Suit;
  value: RankValue;
  rank: number;
  color: CardColor;
  faceDown: boolean;
}

export type PasjansSource =
  | { kind: "waste" }
  | { kind: "foundation"; index: FoundationIndex }
  | { kind: "tableau"; index: TableauIndex; start: number };

export type PasjansDest =
  | { kind: "foundation"; index: FoundationIndex }
  | { kind: "tableau"; index: TableauIndex };

export interface PasjansState {
  stock: PasjansCard[];
  waste: PasjansCard[];
  foundations: [
    PasjansCard[],
    PasjansCard[],
    PasjansCard[],
    PasjansCard[],
  ];
  tableaus: [
    PasjansCard[],
    PasjansCard[],
    PasjansCard[],
    PasjansCard[],
    PasjansCard[],
    PasjansCard[],
    PasjansCard[],
  ];
  drawCount: DrawCount;
  score: number;
  moves: number;
  won: boolean;
}

function colorOf(suit: Suit): CardColor {
  return suit === "♥" || suit === "♦" ? "red" : "black";
}

function cloneCard(card: PasjansCard): PasjansCard {
  return { ...card };
}

function cloneState(state: PasjansState): PasjansState {
  return {
    stock: state.stock.map(cloneCard),
    waste: state.waste.map(cloneCard),
    foundations: [
      state.foundations[0].map(cloneCard),
      state.foundations[1].map(cloneCard),
      state.foundations[2].map(cloneCard),
      state.foundations[3].map(cloneCard),
    ],
    tableaus: [
      state.tableaus[0].map(cloneCard),
      state.tableaus[1].map(cloneCard),
      state.tableaus[2].map(cloneCard),
      state.tableaus[3].map(cloneCard),
      state.tableaus[4].map(cloneCard),
      state.tableaus[5].map(cloneCard),
      state.tableaus[6].map(cloneCard),
    ],
    drawCount: state.drawCount,
    score: state.score,
    moves: state.moves,
    won: state.won,
  };
}

export function clonePasjansState(state: PasjansState): PasjansState {
  return cloneState(state);
}

export function createDeck(): PasjansCard[] {
  const deck: PasjansCard[] = [];
  for (const suit of SUITS) {
    VALUES.forEach((value, index) => {
      deck.push({
        id: `${suit}${value}`,
        suit,
        value,
        rank: index + 1,
        color: colorOf(suit),
        faceDown: true,
      });
    });
  }
  return deck;
}

export function shuffle<T>(items: readonly T[], rng: () => number = Math.random): T[] {
  const next = items.slice();
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const a = next[i];
    const b = next[j];
    if (a === undefined || b === undefined) continue;
    next[i] = b;
    next[j] = a;
  }
  return next;
}

export function newGame(rng: () => number = Math.random): PasjansState {
  const deck = shuffle(createDeck(), rng);
  const tableaus: PasjansState["tableaus"] = [[], [], [], [], [], [], []];

  for (const tableauIndex of TABLEAU_INDEXES) {
    for (let depth = 0; depth <= tableauIndex; depth += 1) {
      const card = deck.pop();
      if (!card) {
        throw new Error("pasjans: deck ran out while dealing");
      }
      tableaus[tableauIndex].push({ ...card, faceDown: depth !== tableauIndex });
    }
  }

  return {
    stock: deck.map((card) => ({ ...card, faceDown: true })),
    waste: [],
    foundations: [[], [], [], []],
    tableaus,
    drawCount: 1,
    score: 0,
    moves: 0,
    won: false,
  };
}

export function toggleDrawCount(state: PasjansState): PasjansState {
  return { ...state, drawCount: state.drawCount === 1 ? 3 : 1 };
}

export function visibleWaste(state: PasjansState): PasjansCard[] {
  return state.waste.slice(-state.drawCount);
}

export function peekCards(state: PasjansState, source: PasjansSource): PasjansCard[] {
  if (source.kind === "waste") {
    const card = state.waste[state.waste.length - 1];
    return card && !card.faceDown ? [card] : [];
  }
  if (source.kind === "foundation") {
    const pile = state.foundations[source.index];
    const card = pile[pile.length - 1];
    return card ? [card] : [];
  }
  const pile = state.tableaus[source.index];
  if (source.start < 0 || source.start >= pile.length) return [];
  const cards = pile.slice(source.start);
  return cards.some((card) => card.faceDown) ? [] : cards;
}

function canPlaceOnFoundation(card: PasjansCard, dest: readonly PasjansCard[]): boolean {
  const top = dest[dest.length - 1];
  if (!top) return card.value === "A";
  return card.suit === top.suit && card.rank === top.rank + 1;
}

function canPlaceOnTableau(card: PasjansCard, dest: readonly PasjansCard[]): boolean {
  const top = dest[dest.length - 1];
  if (!top) return card.value === "K";
  if (top.faceDown) return false;
  return card.color !== top.color && card.rank === top.rank - 1;
}

function isSamePile(source: PasjansSource, dest: PasjansDest): boolean {
  if (source.kind === "waste") return false;
  return source.kind === dest.kind && source.index === dest.index;
}

export function canMove(
  state: PasjansState,
  source: PasjansSource,
  dest: PasjansDest,
): boolean {
  if (state.won) return false;
  if (isSamePile(source, dest)) return false;
  // Foundation-to-foundation is a score exploit in the legacy drag code (Ace onto an
  // empty pile, +10 again). It is not a useful Klondike move, so it is closed here.
  if (source.kind === "foundation" && dest.kind === "foundation") return false;

  const cards = peekCards(state, source);
  const top = cards[0];
  if (!top) return false;

  if (dest.kind === "foundation") {
    if (cards.length !== 1) return false;
    return canPlaceOnFoundation(top, state.foundations[dest.index]);
  }
  return canPlaceOnTableau(top, state.tableaus[dest.index]);
}

export function legalDestinations(
  state: PasjansState,
  source: PasjansSource,
): PasjansDest[] {
  const dests: PasjansDest[] = [];
  for (const index of FOUNDATION_INDEXES) {
    const dest: PasjansDest = { kind: "foundation", index };
    if (canMove(state, source, dest)) dests.push(dest);
  }
  for (const index of TABLEAU_INDEXES) {
    const dest: PasjansDest = { kind: "tableau", index };
    if (canMove(state, source, dest)) dests.push(dest);
  }
  return dests;
}

function takeFromSource(state: PasjansState, source: PasjansSource): PasjansCard[] {
  if (source.kind === "waste") {
    const card = state.waste.pop();
    return card ? [card] : [];
  }
  if (source.kind === "foundation") {
    const card = state.foundations[source.index].pop();
    return card ? [card] : [];
  }

  const pile = state.tableaus[source.index];
  const cards = pile.splice(source.start);
  const revealed = pile[pile.length - 1];
  if (revealed?.faceDown) {
    revealed.faceDown = false;
    state.score += 5;
  }
  return cards;
}

export function tryMove(
  state: PasjansState,
  source: PasjansSource,
  dest: PasjansDest,
): PasjansState | null {
  if (!canMove(state, source, dest)) return null;

  const next = cloneState(state);
  const cards = takeFromSource(next, source);
  const top = cards[0];
  if (!top) return null;

  if (dest.kind === "foundation") {
    next.foundations[dest.index].push(top);
    next.score += 10;
  } else {
    next.tableaus[dest.index].push(...cards);
    if (source.kind === "waste") next.score += 5;
  }

  next.moves += 1;
  next.won = next.foundations.every((pile) => pile.length === 13);
  return next;
}

/** Single face-up card onto the first foundation that will take it. */
export function tryAutoFoundation(
  state: PasjansState,
  source: PasjansSource,
): PasjansState | null {
  for (const index of FOUNDATION_INDEXES) {
    const moved = tryMove(state, source, { kind: "foundation", index });
    if (moved) return moved;
  }
  return null;
}

export function clickStock(state: PasjansState): PasjansState {
  if (state.won) return state;

  if (state.stock.length > 0) {
    const next = cloneState(state);
    const draw = Math.min(next.drawCount, next.stock.length);
    for (let i = 0; i < draw; i += 1) {
      const card = next.stock.pop();
      if (!card) break;
      card.faceDown = false;
      next.waste.push(card);
    }
    next.moves += 1;
    return next;
  }

  if (state.waste.length === 0) return state;

  const next = cloneState(state);
  next.stock = next.waste
    .slice()
    .reverse()
    .map((card) => ({ ...card, faceDown: true }));
  next.waste = [];
  next.score -= 100;
  next.moves += 1;
  return next;
}

export function sameSource(a: PasjansSource, b: PasjansSource): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === "waste") return true;
  if (a.kind === "foundation" && b.kind === "foundation") return a.index === b.index;
  if (a.kind === "tableau" && b.kind === "tableau") {
    return a.index === b.index && a.start === b.start;
  }
  return false;
}

export function destKey(dest: PasjansDest): string {
  return `${dest.kind}:${dest.index}`;
}

export function sourceKey(source: PasjansSource): string {
  if (source.kind === "waste") return "waste";
  if (source.kind === "foundation") return `foundation:${source.index}`;
  return `tableau:${source.index}:${source.start}`;
}

export const PASJANS_MAX_ACTIONS = 2500;

export type PasjansAction =
  | { type: "stock" }
  | { type: "draw" }
  | { type: "move"; source: PasjansSource; dest: PasjansDest }
  | { type: "auto"; source: PasjansSource };

function seedRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function newGameFromSeed(seed: number): PasjansState {
  return newGame(seedRng(seed));
}

export function randomPasjansSeed(): number {
  const bytes = new Uint32Array(1);
  const webCrypto = (globalThis as { crypto?: { getRandomValues(data: Uint32Array): Uint32Array } }).crypto;
  if (webCrypto) {
    webCrypto.getRandomValues(bytes);
    return bytes[0] ?? 0;
  }
  return (Math.random() * 0x100000000) >>> 0;
}

function asFoundationIndex(value: unknown): FoundationIndex | null {
  return value === 0 || value === 1 || value === 2 || value === 3 ? value : null;
}

function asTableauIndex(value: unknown): TableauIndex | null {
  return value === 0 || value === 1 || value === 2 || value === 3 || value === 4 || value === 5 || value === 6
    ? value
    : null;
}

function parseSource(raw: unknown): PasjansSource | null {
  if (typeof raw !== "object" || raw === null) return null;
  const source = raw as Record<string, unknown>;
  if (source.kind === "waste") return { kind: "waste" };
  if (source.kind === "foundation") {
    const index = asFoundationIndex(source.index);
    return index === null ? null : { kind: "foundation", index };
  }
  if (source.kind === "tableau") {
    const index = asTableauIndex(source.index);
    if (index === null || !Number.isInteger(source.start) || (source.start as number) < 0) {
      return null;
    }
    return { kind: "tableau", index, start: source.start as number };
  }
  return null;
}

function parseDest(raw: unknown): PasjansDest | null {
  if (typeof raw !== "object" || raw === null) return null;
  const dest = raw as Record<string, unknown>;
  if (dest.kind === "foundation") {
    const index = asFoundationIndex(dest.index);
    return index === null ? null : { kind: "foundation", index };
  }
  if (dest.kind === "tableau") {
    const index = asTableauIndex(dest.index);
    return index === null ? null : { kind: "tableau", index };
  }
  return null;
}

export function parsePasjansAction(raw: unknown): PasjansAction | null {
  if (typeof raw !== "object" || raw === null) return null;
  const action = raw as Record<string, unknown>;
  if (action.type === "stock") return { type: "stock" };
  if (action.type === "draw") return { type: "draw" };
  if (action.type === "move") {
    const source = parseSource(action.source);
    const dest = parseDest(action.dest);
    if (!source || !dest) return null;
    return { type: "move", source, dest };
  }
  if (action.type === "auto") {
    const source = parseSource(action.source);
    if (!source) return null;
    return { type: "auto", source };
  }
  return null;
}

export function applyPasjansAction(
  state: PasjansState,
  action: PasjansAction,
): PasjansState | null {
  if (action.type === "stock") return clickStock(state);
  if (action.type === "draw") return toggleDrawCount(state);
  if (action.type === "move") return tryMove(state, action.source, action.dest);
  return tryAutoFoundation(state, action.source);
}

export function replayPasjans(
  seed: number,
  actions: readonly PasjansAction[],
): PasjansState | null {
  if (!Number.isInteger(seed) || seed < 0 || seed > 0xffffffff) return null;
  if (actions.length === 0 || actions.length > PASJANS_MAX_ACTIONS) return null;

  let state = newGameFromSeed(seed);
  for (const action of actions) {
    const next = applyPasjansAction(state, action);
    if (!next) return null;
    state = next;
  }
  return state;
}

export function pasjansScoreRejection(input: {
  seed: unknown;
  actions: unknown;
  score: number;
  moves: number;
}): string | null {
  if (!Number.isInteger(input.seed) || (input.seed as number) < 0 || (input.seed as number) > 0xffffffff) {
    return "Nieprawidłowy układ kart";
  }
  if (!Array.isArray(input.actions)) {
    return "Brak przebiegu gry";
  }

  const actions: PasjansAction[] = [];
  for (const raw of input.actions) {
    const action = parsePasjansAction(raw);
    if (!action) return "Nieprawidłowy przebieg gry";
    actions.push(action);
  }

  const played = replayPasjans(input.seed as number, actions);
  if (!played) return "Nieprawidłowy przebieg gry";
  if (!played.won) return "Ta gra nie jest ukończona";
  if (played.score !== input.score || played.moves !== input.moves) {
    return "Wynik nie zgadza się z przebiegiem gry";
  }
  return null;
}

